"use client";

import { useEffect } from "react";
import { searchRequestsSchedulingFocus } from "@/lib/virtualspace/order-tracker-after-accept";

/**
 * Force the page to render at scrollTop = 0 on mount and keep
 * it pinned across the lazy-mount DOM-growth race that
 * affects share pages. Renders nothing.
 *
 * Why this exists
 * ---------------
 * Share pages lazy-mount heavy viewers (Autodesk Viewer,
 * Cesium 3D Tiles, Giro3D point cloud, Google Aerial
 * fly-over video, NavVis IVION iframes) AFTER first paint.
 * Each starts as a fixed-height skeleton and grows when the
 * real component loads. Three failure modes can land the
 * recipient mid-page:
 *
 *   1. Browser scroll-restoration replaying the saved offset
 *      from a previous visit before React hydrates.
 *   2. CSS scroll-anchoring shifting the user as placeholders
 *      are replaced by the real (often taller) content.
 *   3. Embedded iframes calling `scrollIntoView` / `focus()`
 *      on themselves after their content boots.
 *
 * The share-route layout (`src/app/share/layout.tsx`) handles
 * (1) via an inline `<script>` that disables
 * `scrollRestoration` synchronously during HTML parse, and
 * (2) via `overflow-anchor: none` CSS. This component handles
 * (3) by re-pinning the scroll position across the next
 * several frames + delays after hydration.
 *
 * Bail conditions:
 *   - User scrolls (wheel / touchmove / arrow keys / Page
 *     Up / Page Down / Home / End / Space).
 *   - URL has a hash fragment that matches a real DOM element
 *     — the recipient explicitly deep-linked to that section,
 *     overriding it would feel broken.
 *   - Order tracker `?proposal_accepted=1` / `?proposal_deposit=success`
 *     — after-accept should land on scheduling, not the top of the letter.
 *
 * Mounted at the share-route layout level so it covers every
 * `/share/*` page automatically; individual pages don't need
 * to mount it themselves.
 */
export default function ResetScrollOnLoad() {
  useEffect(() => {
    if (typeof window === "undefined") return;

    try {
      window.history.scrollRestoration = "manual";
    } catch {
      // Some browsers / sandboxed iframes throw on assignment
      // — safe to ignore; we still pin via scrollTo below.
    }

    let userScrolled = false;
    const flagUserScroll = () => {
      userScrolled = true;
    };
    const flagOnNavKey = (e: KeyboardEvent) => {
      // Recognise every key that scrolls. `Space` scrolls down
      // by a viewport. `Home` / `End` jump to top / bottom.
      const navKeys = new Set([
        "PageDown",
        "PageUp",
        "ArrowDown",
        "ArrowUp",
        "Space",
        "End",
        "Home",
      ]);
      if (navKeys.has(e.code)) userScrolled = true;
    };

    window.addEventListener("wheel", flagUserScroll, {
      passive: true,
      once: true,
    });
    window.addEventListener("touchmove", flagUserScroll, {
      passive: true,
      once: true,
    });
    window.addEventListener("keydown", flagOnNavKey, { once: true });

    const hasRealHashAnchor = (): boolean => {
      const hash = window.location.hash;
      if (!hash || hash === "#") return false;
      try {
        // Strip the leading `#` and look up by id directly. Avoids
        // `querySelector(hash)` which throws on hashes with chars
        // like `/`, `:`, or unicode that aren't valid CSS.
        return Boolean(document.getElementById(hash.slice(1)));
      } catch {
        return false;
      }
    };

    const requestsOrderTrackerSchedulingFocus = (): boolean => {
      try {
        if (!window.location.pathname.includes("/share/order/")) return false;
        const params = new URLSearchParams(window.location.search);
        return searchRequestsSchedulingFocus({
          proposal_accepted: params.get("proposal_accepted") ?? undefined,
          proposal_deposit: params.get("proposal_deposit") ?? undefined,
        });
      } catch {
        return false;
      }
    };

    /*
      The VirtualSpace renderer's actual scroll surface is an INNER
      `<div class="…overflow-y-auto…">` (the scroll-band managed by
      `VIRTUALSPACE_PUBLIC_SCROLL_CLASS`), not `window`. Pinning
      `window.scrollY=0` alone does nothing about pages that land
      mid-content because the outer document never moved — the
      inner band did. Find every overflow-y-auto descendant under
      `<main>` and pin each to scrollTop=0. Belt-and-suspenders:
      pin window too in case the page is rendered without the
      shell (e.g. a future point-cloud-only `/share/[token]` route
      that scrolls the document directly).
    */
    const findScrollContainers = (): HTMLElement[] => {
      const out: HTMLElement[] = [];
      try {
        const main = document.querySelector("main");
        const candidates = main
          ? Array.from(main.querySelectorAll<HTMLElement>("*"))
          : [];
        for (const el of candidates) {
          const ov = window.getComputedStyle(el).overflowY;
          if ((ov === "auto" || ov === "scroll") && el.clientHeight > 0) {
            out.push(el);
          }
        }
      } catch {
        // Defensive — a bad selector should never break the rest
        // of the share page. Window-only pin still fires below.
      }
      return out;
    };

    const pin = () => {
      if (userScrolled) return;
      if (hasRealHashAnchor()) return;
      if (requestsOrderTrackerSchedulingFocus()) return;
      if (window.scrollY !== 0) {
        window.scrollTo(0, 0);
      }
      for (const el of findScrollContainers()) {
        if (el.scrollTop !== 0) el.scrollTop = 0;
      }
    };

    pin();

    // Re-pin across the next several frames + delays. Heavy
    // viewers (Autodesk, Cesium, point cloud, NavVis iframe,
    // aerial video) take 50–1500 ms to mount; covering that
    // window ensures we override any rogue `scrollIntoView` /
    // `focus()` call from inside an embedded iframe that lands
    // the user mid-page. Bumped the tail to 5000 ms because
    // NavVis IVION's iframe was reliably stealing keyboard
    // focus during boot — when its content document calls
    // `window.focus()` the parent receives focus on the iframe
    // *element*, and on slow connections that fires AFTER the
    // 2500 ms tail had already retired the pinner. The browser
    // then scrolled the parent down to bring the focused
    // iframe into view, landing the customer at scrollY ≈
    // 1200 px instead of 0. Five seconds covers the long tail.
    const rafs: number[] = [
      window.requestAnimationFrame(pin),
      window.requestAnimationFrame(() =>
        window.requestAnimationFrame(pin),
      ),
    ];
    const timeouts = [50, 150, 400, 900, 1500, 2500, 3500, 5000].map(
      (delay) => window.setTimeout(pin, delay),
    );

    /*
      Belt-and-suspenders: any time the page scrolls during the
      first 5 seconds AND the user hasn't shown intent (wheel,
      touch, nav key), snap back to top. This catches the
      worst-case where an embedded iframe (NavVis IVION,
      Matterport, Autodesk) calls `scrollIntoView` / `focus()`
      between two of our scheduled `pin()` ticks. `pin()` is
      already idempotent and bails on `userScrolled` /
      hash-anchor, so the listener is a cheap safety net.
    */
    const onInvoluntaryScroll = () => pin();
    window.addEventListener("scroll", onInvoluntaryScroll, {
      passive: true,
    });
    const scrollGuardTimeout = window.setTimeout(() => {
      window.removeEventListener("scroll", onInvoluntaryScroll);
    }, 5000);

    return () => {
      rafs.forEach((id) => window.cancelAnimationFrame(id));
      timeouts.forEach((id) => window.clearTimeout(id));
      window.clearTimeout(scrollGuardTimeout);
      window.removeEventListener("scroll", onInvoluntaryScroll);
      window.removeEventListener("wheel", flagUserScroll);
      window.removeEventListener("touchmove", flagUserScroll);
      window.removeEventListener("keydown", flagOnNavKey);
    };
  }, []);
  return null;
}
